<template>
  <div class="login form-body">
    <div class="login-box" :class="{ 'has-error': !!feedback }">
      <div class="login-part">
        <div class="center-items" :class="{ 'has-oauth-providers': hasOAuthProviders }">
          <div class="title-part">
            <img class="meDaPro-logo" src="@assets/MeDaPro_logo.svg" alt="" />
          </div>
          <div class="external-login-oauth-wrapper" v-if="hasOAuthProviders">
            <transition name="fade" mode="out-in">
              <div v-if="oAuthLoading" class="d-flex flex-column align-items-center justify-content-center oauth-login-loader">
                <spinner stroke="#0075ba" :size="75"></spinner>
                <div>{{ $t("loggingIn") }}</div>
              </div>
              <div v-else class="external-login-oauth-part">
                <div v-for="provider in oAuthProviders" :key="provider.name" class="login-button-auth-external" v-show="!oAuthError">
                  <b-btn :id="provider.name" @click.prevent="signInWithoAuth(provider)" variant="outline-secondary">
                    <img :src="provider.icon" />
                    {{ $t("oAuthProviders." + provider.name) }}
                  </b-btn>
                </div>
                <div class="external-login-oauth-error text-white" v-show="oAuthError">
                  <a href="#" class="close-btn" @click.prevent="goBack"><i class="mdp-Close"></i></a>
                  <h6>{{ $t("api.errors.oAuthFailed") }}</h6>
                  <p v-show="!!feedback" class="text-error mb-0" data-cy="login-error-text">
                    {{ apiError ? $t("api.errors." + feedback) : feedback }}
                  </p>
                  <div class="actions">
                    <a href="#" class="switch-provider" @click.prevent="goBack"><i class="mdp-ArrowLeft"></i> {{ $t("oAuthProviders.chooseOtherProvider") }}</a>
                    <a href="#" class="retry" @click.prevent="goBack">{{ $t("oAuthProviders.retry") }} <i class="mdp-ArrowRight"></i></a>
                  </div>
                </div>
                <div class="link-signin-medapro" v-if="!showNormalLogin">
                  <a class="text-center" href="#" @click.prevent="showNormalLogin = true" data-cy="login-medapro-account-btn">{{
                    $t("signInWithMedaProAccount")
                  }}</a>
                </div>
              </div>
            </transition>
          </div>
          <transition name="fade">
            <div v-if="showNormalLogin || !hasOAuthProviders" class="forms-part">
              <div class="text-signin-medapro" v-if="hasOAuthProviders">
                <p class="text-center py-0 mb-0">{{ $t("signInWithMedaProAccount") }}</p>
              </div>
              <transition name="fade" mode="out-in">
                <form v-if="!loading">
                  <div class="login-input-wrapper">
                    <div class="login-input-container">
                      <div class="login-input-with-icon-container">
                        <div class="login-input-icon-prepend"><i class="mdp-User"></i></div>
                        <input
                          ref="inputun"
                          class="login-input"
                          :class="{ error: apiError }"
                          @keyup.prevent="onChangeInput"
                          type="text"
                          name="username"
                          :placeholder="$t('userName')"
                          v-model="args.username"
                          data-cy="login-username-text" />
                      </div>
                      <div class="login-input-with-icon-container">
                        <div class="login-input-icon-prepend"><i class="mdp-Password"></i></div>
                        <input
                          ref="inputpw"
                          class="login-input"
                          :class="{ error: apiError }"
                          @keyup.prevent="onChangeInput"
                          type="password"
                          name="password"
                          :placeholder="$t('password')"
                          v-model="args.password"
                          data-cy="login-password-text" />
                      </div>
                    </div>
                    <p v-show="!!feedback && !oAuthError" class="error text-left py-0 mb-0" data-cy="login-error-text">
                      {{ apiError ? $t("api.errors." + feedback) : feedback }}
                    </p>
                  </div>
                  <div class="button-part">
                    <div class="login-button-wrapper">
                      <b-btn variant="primary" id="submit" type="submit" @click.prevent="signIn" data-cy="login-submit-btn"> {{ $t("login") }} </b-btn>
                    </div>
                  </div>
                </form>
                <div v-else class="d-flex flex-column align-items-center justify-content-center login-loader">
                  <spinner stroke="#0075ba" :size="75"></spinner>
                  <div>{{ $t("loggingIn") }}</div>
                </div>
              </transition>
            </div>
          </transition>
        </div>
      </div>
      <div class="footer-part">
        <div>
          <div class="version-label">
            {{ clientVersion }}
          </div>
        </div>
        <img class="itb-logo" src="@assets/logo2.svg" alt="" />
      </div>
    </div>
  </div>
</template>

<script>
import { UserManager } from "oidc-client-ts";
import { mapActions, mapState } from "vuex";
import docExtensions from "../../plugins/document-extensions";
import Spinner from "../components/Spinner.vue";
import { useToast } from "@common/composable/toast";
import moment from "moment";

export default {
  setup() {
    const { createToast, closeToast } = useToast();

    return { createToast, closeToast };
  },
  data() {
    return {
      args: { username: "", password: "" },
      loading: false,
      feedback: "",
      apiError: false,
      oAuthError: false,
      oAuthLoading: false,
      showNormalLogin: false
    };
  },
  components: { Spinner },
  methods: {
    ...mapActions(["login", "loginWithCode"]),
    signInWithoAuth: async function (provider) {
      await provider.userManager.signinRedirect();
    },
    signInWithCode: async function (provider, code) {
      const me = this;
      me.oAuthLoading = true;
      try {
        await this.loginWithCode({ provider, code });
        this.handleLoginSuccess(me);
        this.notifyIfLicenceExpired();
      } catch (ex) {
        me.apiError = false;
        me.oAuthError = true;
        this.handleLoginFailure(me, ex);
        let baseUrl = docExtensions.getFrontendUrl();
        baseUrl += "app/login";
        window.history.replaceState(null, "", baseUrl);
      } finally {
        me.oAuthLoading = false;
      }
    },
    signIn: async function () {
      const me = this;
      if (!me.args.username || !me.args.password) {
        me.apiError = false;
        me.oAuthError = false;
        me.feedback = me.$t("EmptyUserNameOrPassword");
        if (!me.args.username) {
          this.$refs.inputun.classList.add("error");
        }
        if (!me.args.password) {
          this.$refs.inputpw.classList.add("error");
        }
        return;
      }

      me.loading = true;
      try {
        await this.login(this.args);
        this.handleLoginSuccess(me);
        this.notifyIfLicenceExpired();
      } catch (ex) {
        me.feedback = "";
        me.apiError = false;
        me.oAuthError = false;
        this.handleLoginFailure(me, ex);
      } finally {
        me.loading = false;
      }
    },
    handleLoginSuccess(me) {
      me.feedback = "";
      me.apiError = false;
      me.oAuthError = false;
      const next = me.$route.query.go;
      try {
        if (!next) me.$router.push({ name: "dashboard" });
        else me.$router.push(next);
      } catch (error) {
        console.error("navigation error logged. This should not be a problem in most cases", arguments);
      }
    },
    handleLoginFailure(me, ex) {
      console.error("login error", ex);
      if (ex.licenseError) {
        me.feedback = me.$t("api.errors.NoLicense");
        me.args.password = "";
        me.apiError = false;
        return;
      }

      const data = ex.response ? ex.response.data : null;
      if (data?.error === "LicenseExpired") {
        me.feedback = me.$t("licenseExpired", { expiryDate: this.formattedDate(moment(data?.message, "YYYY-MM-DD")) });
      } else if (data?.error === "InvalidEmail") {
        me.feedback = me.$t("oAuthInvalidEmail");
      } else if (data?.error === "InvalidAuthCode" || data?.error === "TokenEndPointAccessFailed") {
        me.feedback = me.$t("oAuthProviderCommunicationFailed");
      } else if (data?.error === "RateLimited") {
        me.feedback = me.$t("RateLimitedUser", { limit: me.getRemainingTime(data.remainingTime) });
      } else {
        me.args.password = "";
        me.feedback = data && data.error ? me.$t(data.error) : ex.message || me.$t("unexpectedLoginFailure");
        me.apiError = !!(data && data.error && me.$te("api.errors." + me.feedback));

        if (ex.response && ex.response.status === 429) {
          const remainingTime = parseInt(ex.response.headers["retry-after"]);
          if (remainingTime && !isNaN(remainingTime)) {
            me.feedback = me.$t("RateLimitedRequest", { limit: me.getRemainingTime(remainingTime) });
          }
        }
      }
    },
    getRemainingTime(time) {
      if (time < 60) {
        return `${time} ${this.$t("RateLimit.seconds")}`;
      }

      const minutes = Math.ceil(time / 60);
      if (minutes < 60) {
        return `${minutes} ${this.$t("RateLimit.minutes")}`;
      }

      const hours = Math.ceil(minutes / 60);
      return `${hours} ${this.$t("RateLimit.hours")}`;
    },
    goBack() {
      this.oAuthError = false;
      this.feedback = "";
    },
    onChangeInput(event) {
      event.target.classList.remove("error");
    },
    notifyIfLicenceExpired() {
      const _licences = this.licenses;
      if (_licences && Object.prototype.hasOwnProperty.call(_licences, "ExpiryDate")) {
        const expiryValue = _licences["ExpiryDate"];
        if (expiryValue) {
          const expiryDate = moment(expiryValue, "YYYY-MM-DD");
          const noOfDaysForExpiration = expiryDate.diff(moment().startOf("day"), "days");
          const formattedExpiryDate = this.formattedDate(expiryDate);

          if (noOfDaysForExpiration == 1) {
            // Licence expire tomorrow
            this.createToast({
              text: this.$t("licenseExpireTomorrowWarning"),
              title: this.$t("licenseExpires", { timePeriod: this.$t("tomorrow").toLowerCase() }),
              id: "toast_from_licence_expire",
              variant: "danger",
              position: "bottom-right",
              autoClose: false,
              titleIcon: "bell-ring",
              bodyVariant: "danger"
            });
          } else if (noOfDaysForExpiration > 1 && noOfDaysForExpiration <= 7) {
            // Licence expire within a week
            this.createToast({
              text: this.$t("licenseExpireWeekWarning", { difference: noOfDaysForExpiration, expiryDate: formattedExpiryDate }),
              title: this.$t("licenseExpires", { timePeriod: this.$t("inAWeek") }),
              id: "toast_from_licence_expire",
              variant: "warning",
              position: "bottom-right",
              titleIcon: "bell-ring",
              autoClose: false,
              bodyVariant: "warning"
            });
          } else if (noOfDaysForExpiration > 7 && noOfDaysForExpiration <= 30) {
            // Licence expire within a month
            this.createToast({
              text: this.$t("licenseExpireMonthWarning", { difference: noOfDaysForExpiration, expiryDate: formattedExpiryDate }),
              title: this.$t("licenseExpires", { timePeriod: this.$t("soon") }),
              id: "toast_from_licence_expire",
              variant: "primary",
              position: "bottom-right",
              titleIcon: "bell-ring",
              autoClose: false,
              bodyVariant: "primary"
            });
          }
        }
      }
    },
    formattedDate(expiryDate) {
      return new Date(expiryDate).toLocaleDateString();
    }
  },
  mounted() {
    const reason = this.$route.query.t;
    const code = this.$route.query.code;
    if (reason && reason.toLowerCase() == "expired") {
      this.feedback = this.$t("SessionExpired");
    } else if (code) {
      this.signInWithCode(this.$route.params.provider, code);
    }
  },
  computed: {
    ...mapState(["licenses"]),
    ...mapState("metadata", ["version", "config"]),
    oAuthProviders() {
      return this.config.oAuthProviders;
    },
    clientVersion() {
      const versionPrefix = "20";
      const versionArray = this.version.split(".").slice(0, 3);

      // Remove trailing zeros
      for (let i = versionArray.length - 1; i >= 0; i--) {
        if (versionArray[i] === "0") {
          versionArray.pop();
        } else {
          break;
        }
      }

      return versionPrefix.concat(versionArray.join("."));
    },
    hasOAuthProviders() {
      return this.oAuthProviders && this.oAuthProviders.length > 0;
    }
  },
  async created() {
    if (this.oAuthProviders) {
      this.oAuthProviders.forEach((provider) => {
        if (!provider.userManager) {
          provider.userManager = new UserManager(provider.config);
        }
      });
    }
  }
};
</script>
